import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, FlaskConical, Sparkles, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { BrandMark } from "@/components/BrandMark";

const HERO_BG =
  "https://images.unsplash.com/photo-1772568842180-02b9a4ca1b36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const pathIcons = [FlaskConical, Sparkles, Compass];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export default function Home() {
  const { content } = useSite();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  if (!content) return <div className="h-screen" />;
  const { hero, paths, mastery } = content;
  const wa = `https://wa.me/${content.whatsapp}`;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="grain relative min-h-[92vh] flex items-center justify-center pt-28 pb-20 px-5 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/70 via-[#08080A]/60 to-[#08080A]" />
        </div>
        <div className="hero-aurora" />
        <div className="gold-orb floaty" style={{ width: 220, height: 220, top: "14%", left: "8%" }} />
        <div className="gold-orb floaty" style={{ width: 140, height: 140, bottom: "16%", left: "42%", animationDelay: "1.5s" }} />
        <div className="gold-orb floaty" style={{ width: 90, height: 90, top: "26%", right: "24%", animationDelay: "0.7s" }} />
        <BrandMark size={520} className="slow-spin pointer-events-none absolute -right-40 top-6 opacity-[0.06]" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 max-w-4xl"
        >
          <p className="text-xs sm:text-sm tracking-[0.3em] uppercase font-mono-accent text-amber-400/80 mb-6">
            {hero.overline}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-amber-50 mb-8">
            {hero.title}
          </h1>
          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-10">
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-reserve-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.03] active:scale-95 transition-transform"
            >
              {hero.cta_primary}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-amber-500/30 text-amber-100 hover:bg-amber-500/10 transition-colors"
            >
              {hero.cta_secondary}
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Three paths */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <motion.p {...fade} className="text-center text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">
          Trois voies, une même quête
        </motion.p>
        <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl text-center text-amber-50 mb-16">
          Science × Spiritualité × Développement personnel
        </motion.h2>
        <div className="grid gap-8 md:grid-cols-3">
          {paths.map((p, i) => {
            const Icon = pathIcons[i % pathIcons.length];
            return (
              <motion.div
                key={p.title}
                {...fade}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="glass card-hover rounded-2xl p-8"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6">
                  <Icon className="text-amber-300" size={22} />
                </div>
                <h3 className="font-serif text-2xl text-amber-100 mb-3">{p.title}</h3>
                <p className="text-slate-400 leading-relaxed">{p.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Mastery steps */}
      <section className="relative py-24 border-y border-amber-500/10 bg-[#0b0b0f] overflow-hidden">
        <div className="section-halo absolute inset-0" />
        <div className="max-w-7xl mx-auto px-5 relative">
          <motion.p {...fade} className="text-center text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">
            Le chemin de l'éveil
          </motion.p>
          <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl text-center text-amber-50 mb-16">
            Un chemin clair vers la maîtrise de soi
          </motion.h2>
          <div className="grid gap-8 md:grid-cols-3">
            {mastery.map((m, i) => (
              <motion.div key={m.step} {...fade} transition={{ duration: 0.6, delay: i * 0.12 }} className="relative">
                <span className="font-mono-accent text-6xl text-amber-500/20 block mb-4">{m.step}</span>
                <h3 className="font-serif text-2xl text-amber-100 mb-3">{m.title}</h3>
                <p className="text-slate-400 leading-relaxed">{m.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-3">Nos services</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-amber-50">Formations, accompagnement &amp; consultations</h2>
          </div>
          <Link to="/services" className="text-amber-300 hover:text-amber-200 inline-flex items-center gap-2">
            Tout voir <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {services.slice(0, 3).map((s, i) => (
            <motion.div key={s.id} {...fade} transition={{ duration: 0.6, delay: i * 0.1 }} className="glass card-hover rounded-2xl p-8 flex flex-col">
              {s.tag && <span className="text-xs font-mono-accent uppercase tracking-widest text-amber-400/80 mb-3">{s.tag}</span>}
              <h3 className="font-serif text-2xl text-amber-100 mb-3">{s.title}</h3>
              <p className="text-slate-400 leading-relaxed flex-1">{s.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-5 py-24 text-center">
        <motion.h2 {...fade} className="font-serif text-3xl sm:text-5xl text-amber-50 mb-6">
          Commencez votre chemin intérieur aujourd'hui
        </motion.h2>
        <motion.p {...fade} className="text-slate-400 text-lg mb-10">
          Un premier pas suffit. Réservez une consultation ou écrivez-nous, et avançons ensemble.
        </motion.p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/contact" className="px-8 py-4 rounded-full bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.03] transition-transform">
            Réserver une consultation
          </Link>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors">
            <MessageCircle size={18} /> Parler sur WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
